import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import icons from './shared/Icons';
import Section from './shared/Section';
import SectionHeading from './shared/SectionHeading';

const integrations = [
  { name: 'Salesforce', category: 'CRM', desc: 'Bi-directional sync of contacts, opportunities, and custom objects. Real-time event streaming supported.' },
  { name: 'Snowflake', category: 'Data Warehouse', desc: 'Native connector with push-down query optimization. Zero-copy data sharing enabled.' },
  { name: 'AWS', category: 'Cloud', desc: 'S3, Redshift, SageMaker, Lambda integrations. IAM role-based access with cross-account support.', image: process.env.PUBLIC_URL + '/icon logo.webp' },
  { name: 'Azure', category: 'Cloud', desc: 'Synapse, Blob Storage, and Azure ML pipelines. Active Directory SSO integration.' },
  { name: 'Google Cloud', category: 'Cloud', desc: 'BigQuery, Cloud Storage, Vertex AI. Service account authentication with VPC peering.', image: process.env.PUBLIC_URL + '/icon logo (2).webp' },
  { name: 'Databricks', category: 'Data Platform', desc: 'Unity Catalog integration. Shared Spark clusters with Delta Lake table support.', image: process.env.PUBLIC_URL + '/icon logo (3).webp' },
  { name: 'Slack', category: 'Communication', desc: 'Alert channels, interactive bot commands, and scheduled report delivery to channels.', image: process.env.PUBLIC_URL + '/icon logo (4).webp' },
  { name: 'Tableau', category: 'Visualization', desc: 'Live connection and extract refresh. Embedded analytics with SSO pass-through.', image: process.env.PUBLIC_URL + '/icon logo (5).webp' },
  { name: 'PostgreSQL', category: 'Database', desc: 'CDC (Change Data Capture) support. Logical replication with schema evolution handling.', image: process.env.PUBLIC_URL + '/icon logo (6).webp' },
  { name: 'MongoDB', category: 'Database', desc: 'Atlas and self-hosted support. Change streams for real-time document sync.' },
  { name: 'Kafka', category: 'Streaming', desc: 'Consumer and producer modes. Schema Registry integration with Avro/Protobuf support.' },
  { name: 'Kubernetes', category: 'Infrastructure', desc: 'Helm chart deployment. Custom resource definitions for pipeline orchestration.' },
  { name: 'Docker', category: 'Infrastructure', desc: 'Pre-built images for all connectors. Docker Compose templates for local dev.' },
  { name: 'Terraform', category: 'Infrastructure', desc: 'Full IaC provider. Manage data pipelines, permissions, and schedules as code.', image: process.env.PUBLIC_URL + '/icon logo (8).webp' },
  { name: 'GitHub', category: 'DevOps', desc: 'CI/CD pipeline triggers. Git-backed version control for ML model configurations.' },
  { name: 'Jira', category: 'Project Mgmt', desc: 'Automated ticket creation from anomaly alerts. Sprint analytics dashboard.', image: process.env.PUBLIC_URL + '/icon logo (9).webp' },
];

/* ── Hexagon clip path ── */
const hexClip = 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)';

/* ── Hex card ── */
const HexCard = ({ item, index, onSelect }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      className="flex justify-center"
      initial={{ opacity: 0, scale: 0.7 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.3, delay: index * 0.04 }}
    >
      <button
        onClick={() => onSelect(item)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="relative w-28 h-32 sm:w-32 sm:h-36 group focus:outline-none"
        aria-label={`View ${item.name} integration details`}
      >
        {/* Hex background */}
        <div
          className="absolute inset-0 transition-all duration-400"
          style={{
            clipPath: hexClip,
            background: hovered
              ? 'linear-gradient(135deg, rgba(8,203,0,0.25), rgba(84,227,70,0.15))'
              : 'rgba(17,17,17,0.7)',
            border: 'none',
          }}
        />
        {/* Hex border overlay */}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 115" fill="none" aria-hidden="true">
          <polygon
            points="50,2 98,28.75 98,86.25 50,113 2,86.25 2,28.75"
            className={`transition-colors duration-400 ${hovered ? 'stroke-primary' : 'stroke-dark-border'}`}
            strokeWidth="2"
            fill="none"
          />
        </svg>
        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full gap-2">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm transition-all duration-300 ${
            hovered ? 'bg-primary/20 text-primary scale-110' : 'bg-primary/10 text-primary'
          }`}>
            {item.image ? (
              <img src={encodeURI(item.image)} alt={`${item.name} logo`} className="w-full h-full object-contain" />
            ) : (
              icons.integrationLogos[item.name] || item.name.charAt(0)
            )}
          </div>
          <span className={`text-xs font-medium transition-colors duration-300 ${hovered ? 'text-white' : 'text-gray-400'}`}>
            {item.name}
          </span>
        </div>
      </button>
    </motion.div>
  );
};

/* ── Popover ── */
const Popover = ({ item, onClose }) => (
  <motion.div
    className="fixed inset-0 z-50 flex items-center justify-center p-4"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
    <motion.div
      className="relative glass-card rounded-2xl p-8 max-w-md w-full z-10"
      initial={{ scale: 0.85, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.85, opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        aria-label="Close"
      >
        {icons.close}
      </button>
      <div className="flex items-center gap-4 mb-5">
        <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center text-primary font-bold text-lg">
          {item.image ? (
            <img src={encodeURI(item.image)} alt={`${item.name} logo`} className="w-10 h-10 object-contain" />
          ) : (
            icons.integrationLogos[item.name] || item.name.charAt(0)
          )}
        </div>
        <div>
          <h3 className="text-lg font-bold">{item.name}</h3>
          <span className="text-xs font-mono text-primary/60 uppercase tracking-wider">{item.category}</span>
        </div>
      </div>
      <p className="text-gray-400 text-sm leading-relaxed mb-5">{item.desc}</p>
      <div className="flex gap-3 text-xs">
        <span className="px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary">Bi-directional</span>
        <span className="px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary">Real-time</span>
        <span className="px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary">Certified</span>
      </div>
    </motion.div>
  </motion.div>
);

/* ── Mobile expand card ── */
const MobileIntCard = ({ item, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      className="glass-card rounded-xl overflow-hidden"
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.3, delay: index * 0.04 }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-4 text-left"
      >
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-sm flex-shrink-0">
          {item.image ? (
            <img src={encodeURI(item.image)} alt={`${item.name} logo`} className="w-6 h-6 object-contain" />
          ) : (
            icons.integrationLogos[item.name] || item.name.charAt(0)
          )}
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-semibold">{item.name}</span>
          <span className="block text-[11px] text-gray-500">{item.category}</span>
        </div>
        <motion.svg
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          className="w-4 h-4 text-primary/40 flex-shrink-0"
          animate={{ rotate: open ? 180 : 0 }}
        >
          <path d="M6 9l6 6 6-6" />
        </motion.svg>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">
              <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ── Main ── */
const Integrations = () => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(
    () => integrations.filter((i) =>
      i.name.toLowerCase().includes(search.toLowerCase()) ||
      i.category.toLowerCase().includes(search.toLowerCase())
    ),
    [search]
  );

  return (
    <Section id="integrations" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          tag="Integrations"
          title={<>Connects With Your <span className="gradient-text">Entire Stack</span></>}
          desc="200+ pre-built connectors. Plug into your existing tools in minutes, not days."
        />

        {/* Search bar */}
        <div className="max-w-md mx-auto mb-12">
          <div className="relative">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search integrations..."
              className="w-full bg-dark-card border border-dark-border rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder-gray-500 outline-none focus:border-primary/50 transition-colors"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                aria-label="Clear search"
              >
                {icons.close}
              </button>
            )}
          </div>
        </div>

        {/* Desktop/Tablet honeycomb grid */}
        <div className="hidden sm:grid grid-cols-4 lg:grid-cols-8 gap-y-2 gap-x-1">
          {filtered.map((item, i) => (
            <HexCard key={item.name} item={item} index={i} onSelect={setSelected} />
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              No integrations match "{search}"
            </div>
          )}
        </div>

        {/* Mobile 2-col grid */}
        <div className="sm:hidden grid grid-cols-2 gap-3">
          {filtered.map((item, i) => (
            <MobileIntCard key={item.name} item={item} index={i} />
          ))}
          {filtered.length === 0 && (
            <div className="col-span-2 text-center py-8 text-gray-500 text-sm">
              No integrations match "{search}"
            </div>
          )}
        </div>
      </div>

      {/* Popover */}
      <AnimatePresence>
        {selected && <Popover item={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </Section>
  );
};

export default Integrations;
